import ApartmentOutlined from '@ant-design/icons/lib/icons/ApartmentOutlined'
import ClockCircleOutlined from '@ant-design/icons/lib/icons/ClockCircleOutlined'
import UserOutlined from '@ant-design/icons/lib/icons/UserOutlined'
import { Button, Timeline } from 'antd'
import { inject, observer } from 'mobx-react'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import FileItem from '../../../../components/Common/FileItemComponent/FileItem'
import { ELECTRONIC_DOCUMENT_STATUS } from '../../../../constants'
import dayjs from '../../../../dayjsConfig'
import utils from '../../../../utils'

const ELECTRONIC_STATUS = [
  'PENDING',
  'CRE_APPROVAL',
  'TP_APPROVAL',
  'VT_APPROVAL',
  'VT_SIGNED',
  'VT_REJECTION',
  'LEAD_APPROVAL',
  'LEAD_REJECTION',
  'REGIS_NUMBER',
  'REGIS_NUMBER_REJECTION',
  'READY',
  'READY_REJECTION',
  'RELEASE',
]

const STATUS_MAPPING = {
  [ELECTRONIC_DOCUMENT_STATUS.PENDING]: 'Chưa xử lý',
  [ELECTRONIC_DOCUMENT_STATUS.CHANGE_DOC]: 'Thay đổi tài liệu',
  [ELECTRONIC_DOCUMENT_STATUS.CRE_APPROVAL]: 'Đã trình dự thảo',
  [ELECTRONIC_DOCUMENT_STATUS.TP_APPROVAL]: 'Trưởng phòng đã ký số',
  [ELECTRONIC_DOCUMENT_STATUS.TP_REVERT]: 'Trưởng phòng hủy duyệt',
  [ELECTRONIC_DOCUMENT_STATUS.TP_CO_SIGN]: 'Trưởng phòng ký',
  [ELECTRONIC_DOCUMENT_STATUS.KTAT_OFFICE_APPROVAL]: 'Phòng KTAT đã duyệt',
  [ELECTRONIC_DOCUMENT_STATUS.KTAT_OFFICE_REJECTION]:
    'Phòng KTAT từ chối duyệt',
  [ELECTRONIC_DOCUMENT_STATUS.LEAD_KTAT_OFFICE_APPROVAL]:
    'Trưởng phòng KTAT đã duyệt',
  [ELECTRONIC_DOCUMENT_STATUS.LEAD_KTAT_OFFICE_REJECTION]:
    'Trưởng phòng KTAT từ chối duyệt',
  [ELECTRONIC_DOCUMENT_STATUS.FORM_APPROVAL]: 'Đã duyệt thể thức',
  [ELECTRONIC_DOCUMENT_STATUS.FORM_REJECTION]: 'Từ chối duyệt thể thức',
  [ELECTRONIC_DOCUMENT_STATUS.BOARD_OF_DIRECTOR_APPROVAL]:
    'Ban giám đốc đã duyệt',
  [ELECTRONIC_DOCUMENT_STATUS.BOARD_OF_DIRECTOR_REJECTION]: 'Hủy cấp số',
  [ELECTRONIC_DOCUMENT_STATUS.ROLLBACK_TO_CREATOR]:
    'Từ chối — Trả về Bước 1 (Người lập)',
  [ELECTRONIC_DOCUMENT_STATUS.ROLLBACK_TO_LEADER]:
    'Từ chối — Trả về Bước 2 (Trưởng đơn vị)',
  [ELECTRONIC_DOCUMENT_STATUS.FLOW_LOCKED]: 'Khóa luồng trình ký',
  [ELECTRONIC_DOCUMENT_STATUS.FLOW_UNLOCK]: 'Mở lại luồng trình ký',
  [ELECTRONIC_DOCUMENT_STATUS.VT_APPROVAL]: 'Văn thư đã duyệt',
  [ELECTRONIC_DOCUMENT_STATUS.VT_SIGNED]: 'Văn thư đã ký số',
  [ELECTRONIC_DOCUMENT_STATUS.VT_REJECTION]: 'Văn thư hủy duyệt',
  [ELECTRONIC_DOCUMENT_STATUS.LEAD_APPROVAL]: 'Trưởng đơn vị đã duyệt',
  [ELECTRONIC_DOCUMENT_STATUS.LEAD_REJECTION]: 'Lãnh đạo hủy duyệt',
  [ELECTRONIC_DOCUMENT_STATUS.REGIS_NUMBER]: 'Đã cấp số',
  [ELECTRONIC_DOCUMENT_STATUS.REGIS_NUMBER_REVERT]: 'Hủy cấp số',
  [ELECTRONIC_DOCUMENT_STATUS.REGIS_NUMBER_REJECTION]: 'Cấp lại số',
  [ELECTRONIC_DOCUMENT_STATUS.READY]: 'Chờ phát hành',
  [ELECTRONIC_DOCUMENT_STATUS.READY_REJECTION]: 'Đóng dấu lại',
  [ELECTRONIC_DOCUMENT_STATUS.RELEASE]: 'Đã phát hành',
  [ELECTRONIC_DOCUMENT_STATUS.RELEASE_REJECTION]: 'Phát hành lại',
  [ELECTRONIC_DOCUMENT_STATUS.ANNEX_DOC]: 'Đã xử lý phụ lục đi kèm',
  [ELECTRONIC_DOCUMENT_STATUS.ANNEX_REGIS_NUMBER]: 'Đã cấp số phụ lục',
  [ELECTRONIC_DOCUMENT_STATUS.ANNEX_READY]: 'Đã ký số phụ lục',
  [ELECTRONIC_DOCUMENT_STATUS.CANCEL]: 'Đã hủy',

  FILE_SIGNED_LEADER_SIGN: '✍️ Trưởng đơn vị ký số',
  FILE_SIGNED_LEAD_KTAT_SIGN: '✍️ TP.KTAT ký số',
  FILE_SIGNED_BOD_SIGN: '✍️ Giám đốc ký số',
  FILE_SIGNED_DIGITAL_SIGN: '🔏 Đóng dấu điện tử',
  FILE_SIGNED_REGIS_NUMBER: '🔢 Cấp số văn bản',
}

const SIGN_STEP_COLORS = {
  LEADER_SIGN: 'purple',
  LEAD_KTAT_SIGN: 'geekblue',
  BOD_SIGN: 'volcano',
  DIGITAL_SIGN: 'green',
  REGIS_NUMBER: 'gold',
}

const DEFAULT_USER_NAME = 'Admin vpdt'
const DEFAULT_DEPARTMENT = 'Mặc định'

/** Số file hiển thị trước khi cần bấm "Xem thêm" */
const HISTORY_FILES_PREVIEW = 5
const HISTORY_FILES_LIST_MAX_HEIGHT = 280

const renderFileItem = (file, key) => (
  <div key={key} style={{ marginBottom: 4 }}>
    <FileItem
      file_id={file.fileId}
      file_name={file.fileName}
      file_type={utils.getExtensionFile(file.fileName)}
    />
  </div>
)

const CollapsibleFileList = memo(({ files, keyPrefix = 'file' }) => {
  const [expanded, setExpanded] = useState(false)

  if (!files?.length) return null

  const hasMore = files.length > HISTORY_FILES_PREVIEW
  const visibleFiles =
    expanded || !hasMore ? files : files.slice(0, HISTORY_FILES_PREVIEW)
  const hiddenCount = files.length - HISTORY_FILES_PREVIEW

  return (
    <div style={{ marginTop: 4 }}>
      {hasMore && (
        <div
          style={{
            fontSize: 12,
            color: '#8c8c8c',
            marginBottom: 6,
          }}>
          {files.length} tài liệu
        </div>
      )}
      <div
        style={{
          maxHeight:
            expanded && files.length > HISTORY_FILES_PREVIEW
              ? HISTORY_FILES_LIST_MAX_HEIGHT
              : undefined,
          overflowY:
            expanded && files.length > HISTORY_FILES_PREVIEW
              ? 'auto'
              : undefined,
          paddingRight: expanded ? 4 : 0,
        }}>
        {visibleFiles.map((file, index) =>
          renderFileItem(file, `${keyPrefix}-${file.fileId}-${index}`)
        )}
      </div>
      {hasMore && (
        <Button
          type="link"
          size="small"
          style={{ padding: 0, height: 'auto', marginTop: 4 }}
          onClick={() => setExpanded(prev => !prev)}>
          {expanded
            ? 'Thu gọn'
            : `Xem thêm (${hiddenCount} file)`}
        </Button>
      )}
    </div>
  )
})
CollapsibleFileList.displayName = 'CollapsibleFileList'

const getActionColor = action => {
  if (!action) return 'blue'
  if (action.startsWith('FILE_SIGNED_')) return 'cyan'
  switch (action) {
    case 'PENDING':
      return 'red'
    case 'RELEASE':
      return 'green'
    case 'ROLLBACK_TO_CREATOR':
    case 'ROLLBACK_TO_LEADER':
    case 'FLOW_LOCKED':
      return 'red'
    case 'FLOW_UNLOCK':
      return 'green'
    default:
      return 'blue'
  }
}

const parseReasonFromDataAfter = dataAfter => {
  if (!dataAfter) return null
  try {
    const parsedData =
      typeof dataAfter === 'string' ? JSON.parse(dataAfter) : dataAfter
    const data = Array.isArray(parsedData) ? parsedData[0] : parsedData
    return data?.reason || null
  } catch (e) {
    return null
  }
}

const parseFilesFromDataAfter = (dataAfter, action) => {
  if (!dataAfter) return []

  try {
    const parsedData = JSON.parse(dataAfter)
    const files = []

    if (action && action.startsWith('FILE_SIGNED_')) {
      const data = Array.isArray(parsedData) ? parsedData[0] : parsedData

      const newFileId = data.newFileId
      const newFileName =
        data.newFileName || `File đã ký (index ${data.signedFileIndex ?? ''})`
      const originalFileId = data.originalFileId
      const signStep = data.signStep

      if (newFileId) {
        files.push({
          fileId: newFileId,
          fileName: newFileName,
          isSignedVersion: true,
          signStep,
          signedBy: data.signedBy,
          signedAt: data.signedAt,
          registerCode: data.registerCode,
          originalFileId,
          signedFileIndex: data.signedFileIndex,
        })
      }
      return files
    }

    const items = Array.isArray(parsedData) ? parsedData : [parsedData]

    items.forEach(item => {
      if (item.mainFiles && Array.isArray(item.mainFiles)) {
        item.mainFiles.forEach(file => {
          const fileId = file.fileId || file.file_id
          const fileName = file.fileName || file.file_name

          if (fileId) {
            files.push({
              fileId,
              fileName: fileName || 'Tài liệu',
              isMainFile: true,
              leaderSigned: file.leaderSigned || file.leader_signed || false,
              leadKtatSigned:
                file.leadKtatSigned || file.lead_ktat_signed || false,
              bodSigned: file.bodSigned || file.bod_signed || false,
              digitalSigned: file.digitalSigned || file.digital_signed || false,
              numberRegistered:
                file.numberRegistered || file.number_registered || false,
              registeredNumber: file.registeredNumber,
              registeredDate: file.registeredDate,
              originFileId: file.originFileId || file.origin_file_id,
            })
          }
        })
      }

      // annexAttachments
      if (item.annexAttachments && Array.isArray(item.annexAttachments)) {
        item.annexAttachments.forEach(file => {
          const fileId = file.fileId || file.file_id
          if (fileId) {
            files.push({
              fileId,
              fileName: file.fileName || file.file_name || 'Phụ lục',
              isAnnex: true,
            })
          }
        })
      }

      // attachments
      if (item.attachments && Array.isArray(item.attachments)) {
        item.attachments.forEach(file => {
          const fileId = file.fileId || file.file_id
          if (fileId) {
            files.push({
              fileId,
              fileName: file.fileName || file.file_name || 'Đính kèm',
              isAttachment: true,
            })
          }
        })
      }
    })

    return files
  } catch (e) {
    console.error('Error parsing data_after:', e)
    return []
  }
}

const getLogUserName = item =>
  item.user?.user_name || item.user_name || 'unknown'

/** Gộp các log ký số liên tiếp (cùng action + user) — batch ký tất cả */
const groupSignedFileHistory = items => {
  const grouped = []
  let index = 0

  while (index < items.length) {
    const current = items[index]

    if (!current.action?.startsWith('FILE_SIGNED_')) {
      grouped.push(current)
      index += 1
      continue
    }

    const batch = [current]
    const { action } = current
    const userName = getLogUserName(current)
    let nextIndex = index + 1

    while (nextIndex < items.length) {
      const next = items[nextIndex]
      if (
        next.action === action &&
        getLogUserName(next) === userName &&
        next.action?.startsWith('FILE_SIGNED_')
      ) {
        batch.push(next)
        nextIndex += 1
      } else {
        break
      }
    }

    if (batch.length === 1) {
      grouped.push(current)
    } else {
      grouped.push({
        ...current,
        id: `signed-group-${batch.map(log => log.id).join('-')}`,
        isGroupedSignedLog: true,
        groupedLogs: batch,
      })
    }

    index = nextIndex
  }

  return grouped
}

const collectFilesFromHistoryItem = item => {
  if (item.isGroupedSignedLog && item.groupedLogs?.length) {
    const files = item.groupedLogs.flatMap(log =>
      parseFilesFromDataAfter(log.data_after, log.action)
    )
    return files
      .sort(
        (a, b) =>
          (a.signedFileIndex ?? Number.MAX_SAFE_INTEGER) -
          (b.signedFileIndex ?? Number.MAX_SAFE_INTEGER)
      )
      .map(file => ({ ...file, isMainFile: true }))
  }

  return parseFilesFromDataAfter(item.data_after, item.action)
}

const FileRenderer = memo(({ item }) => {
  const allFiles = useMemo(
    () => collectFilesFromHistoryItem(item),
    [item]
  )

  if (allFiles.length === 0) return null

  if (item.isGroupedSignedLog || item.action?.startsWith('FILE_SIGNED_')) {
    return <CollapsibleFileList files={allFiles} keyPrefix="signed" />
  }

  // Với action thông thường: hiển thị danh sách mainFiles (rút gọn nếu nhiều)
  const mainFiles = allFiles.filter(f => f.isMainFile)
  const otherFiles = allFiles.filter(f => !f.isMainFile)

  return (
    <div>
      <CollapsibleFileList files={mainFiles} keyPrefix="main" />
      {otherFiles.length > 0 && (
        <CollapsibleFileList files={otherFiles} keyPrefix="other" />
      )}
    </div>
  )
})
FileRenderer.displayName = 'FileRenderer'

const TimelineItemComponent = memo(
  ({ item, getFullNameByUserName, getDepartmentByUserName }) => {
    const text = STATUS_MAPPING[item.action] || item.action || 'Không xác định'
    const userName = getLogUserName(item)
    const actionColor = getActionColor(item.action)
    const processedDate = dayjs(item.processed_date).format(
      'dddd, DD/MM/YYYY, HH:mm:ss'
    )

    return (
      <Timeline.Item
        key={item.id}
        dot={<ClockCircleOutlined style={{ fontSize: '16px' }} />}
        color={actionColor}>
        <div
          style={{
            marginBottom: 4,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
          <span style={{ fontWeight: 600 }}>{text}</span>
        </div>

        <span style={{ color: 'gray' }}>
          {utils.getNameInCapitalize(processedDate)}
        </span>

        <div style={{ display: 'flex', alignItems: 'center' }}>
          <UserOutlined style={{ marginRight: 8 }} />
          <span>
            {utils.getNameInCapitalize(getFullNameByUserName(userName))}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
          <ApartmentOutlined style={{ marginRight: 8 }} />
          <span>{getDepartmentByUserName(userName)}</span>
        </div>

        {(() => {
          const reason = parseReasonFromDataAfter(item.data_after)
          if (!reason) return null
          return (
            <div
              style={{
                marginBottom: 8,
                padding: '8px 12px',
                background: '#fff1f0',
                border: '1px solid #ffa39e',
                borderRadius: 4,
              }}>
              <div style={{ fontWeight: 600, marginBottom: 4, color: '#cf1322' }}>
                Lý do từ chối:
              </div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{reason}</div>
            </div>
          )
        })()}

        <FileRenderer item={item} />
      </Timeline.Item>
    )
  }
)
TimelineItemComponent.displayName = 'TimelineItemComponent'

const InternalDocumentSignDocHistory = ({
  loadingAnimationStore,
  authenticationStore,
  departmentStore,
  userStore,
  historyLists,
}) => {
  const [userMap, setUserMap] = useState(new Map())

  useEffect(() => {
    let isMounted = true

    const fetchUserData = async () => {
      try {
        loadingAnimationStore.showSpinner(true)

        await departmentStore.getDepartmentWithUsers(
          authenticationStore.currentCompanyCode()
        )

        if (!isMounted) return

        const userLookupMap = new Map()
        departmentStore.departmentList
          .filter(dept => dept.users?.length > 0)
          .forEach(dept => {
            dept.users.forEach(user => {
              userLookupMap.set(user.user_name, {
                fullName: user.name_uppercase,
                department: dept.name,
              })
            })
          })

        setUserMap(userLookupMap)
      } catch (error) {
        console.error('Error fetching user list:', error)
      } finally {
        if (isMounted) {
          loadingAnimationStore.showSpinner(false)
        }
      }
    }

    fetchUserData()
    return () => {
      isMounted = false
    }
  }, [authenticationStore, departmentStore, loadingAnimationStore])

  const sortedHistoryList = useMemo(() => {
    return [...historyLists]
      .filter(item => {
        if (item.action?.startsWith('FILE_SIGNED_')) {
          return item.status !== false && item.status !== 'false'
        }
        return true
      })
      .sort((a, b) => {
        return (b.id || 0) - (a.id || 0)
      })
  }, [historyLists])

  const getFullNameByUserName = useCallback(
    userName => userMap.get(userName)?.fullName || DEFAULT_USER_NAME,
    [userMap]
  )

  const getDepartmentByUserName = useCallback(
    userName => userMap.get(userName)?.department || DEFAULT_DEPARTMENT,
    [userMap]
  )

  const displayHistoryList = useMemo(
    () => groupSignedFileHistory(sortedHistoryList),
    [sortedHistoryList]
  )

  const timelineItems = useMemo(() => {
    return displayHistoryList.map(item => (
      <TimelineItemComponent
        key={item.id}
        item={item}
        getFullNameByUserName={getFullNameByUserName}
        getDepartmentByUserName={getDepartmentByUserName}
      />
    ))
  }, [displayHistoryList, getFullNameByUserName, getDepartmentByUserName])

  return <Timeline mode="left">{timelineItems}</Timeline>
}

export {
  ELECTRONIC_STATUS as electronicStatus,
  STATUS_MAPPING as getStatusMapping,
}

export default memo(
  inject(
    'loadingAnimationStore',
    'authenticationStore',
    'userStore',
    'listHistoryLogStore',
    'departmentStore'
  )(observer(InternalDocumentSignDocHistory))
)
